import {
  Box, Container, Heading, Button, Card, CardBody, VStack, HStack,
  FormControl, FormLabel, Input, Textarea, Select, useToast,
  IconButton, Text, Divider, Alert, AlertIcon, Badge, Table,
  Thead, Tbody, Tr, Th, Td, Checkbox
} from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { criarListaCompraManual, getProdutos } from "../services/api";
import { FiArrowLeft, FiPlus, FiTrash2, FiSearch } from "react-icons/fi";

export default function NovaListaCompra() {
  const navigate = useNavigate();
  const toast = useToast();

  const [criando, setCriando] = useState(false);
  const [fornecedor, setFornecedor] = useState("");
  const [dataLista, setDataLista] = useState(new Date().toISOString().split('T')[0]);
  const [observacoes, setObservacoes] = useState("");
  const [itens, setItens] = useState([]);

  // Estado para buscar produtos
  const [buscandoProduto, setBuscandoProduto] = useState(false);
  const [codigoBusca, setCodigoBusca] = useState("");
  const [produtoEncontrado, setProdutoEncontrado] = useState(null);

  const handleBuscarProduto = async () => {
    if (!codigoBusca.trim()) {
      toast({
        title: "Digite um código",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setBuscandoProduto(true);
    const produtos = await getProdutos(codigoBusca);
    setBuscandoProduto(false);

    if (produtos && produtos.length > 0) {
      setProdutoEncontrado(produtos[0]);
    } else {
      toast({
        title: "Produto não encontrado",
        description: `Nenhum produto encontrado com o código "${codigoBusca}"`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setProdutoEncontrado(null);
    }
  };

  const handleAdicionarItem = () => {
    if (!produtoEncontrado) {
      toast({
        title: "Busque um produto primeiro",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Verifica se o produto já foi adicionado
    if (itens.find(item => item.produto_id === produtoEncontrado.id)) {
      toast({
        title: "Produto já adicionado",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const novoItem = {
      produto_id: produtoEncontrado.id,
      codigo_global: produtoEncontrado.codigo_global || produtoEncontrado.codigo,
      nome_produto: produtoEncontrado.nome,
      quantidade_solicitada: 1,
      marca_solicitada: produtoEncontrado.marca || "",
      codigo_fornecedor: produtoEncontrado.codigo_fornecedor || "",
      preco_referencia: produtoEncontrado.preco_venda || 0,
      preco_maximo: 0,
      urgente: false,
      observacoes: ""
    };

    setItens([...itens, novoItem]);
    setCodigoBusca("");
    setProdutoEncontrado(null);

    toast({
      title: "Produto adicionado",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleRemoverItem = (index) => {
    setItens(itens.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index, field, value) => {
    const novosItens = [...itens];
    novosItens[index][field] = value;
    setItens(novosItens);
  };

  const handleCriarLista = async () => {
    // Validações
    if (!fornecedor || fornecedor.trim() === "") {
      toast({
        title: "Fornecedor obrigatório",
        description: "Informe o nome do fornecedor",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!dataLista) {
      toast({
        title: "Data obrigatória",
        description: "Informe a data da lista",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (itens.length === 0) {
      toast({
        title: "Adicione itens",
        description: "A lista precisa ter pelo menos 1 item",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setCriando(true);
    const payload = {
      fornecedor_id: parseInt(fornecedor) || null, // Se não tiver ID, usar null
      data_lista: dataLista,
      observacoes: observacoes,
      itens: itens.map(item => ({
        ...item,
        quantidade_solicitada: parseInt(item.quantidade_solicitada),
        preco_referencia: parseFloat(item.preco_referencia) || 0,
        preco_maximo: parseFloat(item.preco_maximo) || 0
      }))
    };

    const resultado = await criarListaCompraManual(payload);
    setCriando(false);

    if (resultado) {
      toast({
        title: "Lista criada com sucesso!",
        description: `Hash: ${resultado.lista?.hash}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate('/listas-compras');
    } else {
      toast({
        title: "Erro ao criar lista",
        description: "Tente novamente",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="#f0f0f0" ml={{ base: 0, md: "64px" }} pt={{ base: "60px", md: 6 }} pb={6} px={4}>
      <Container maxW="container.xl">
        {/* Header */}
        <HStack justifyContent="space-between" mb={6}>
          <HStack>
            <IconButton
              icon={<FiArrowLeft />}
              onClick={() => navigate('/listas-compras')}
              variant="ghost"
              aria-label="Voltar"
            />
            <Heading size="lg" color="#01695E">Nova Lista de Compras</Heading>
          </HStack>
          <Button
            colorScheme="green"
            bg="#01695E"
            onClick={handleCriarLista}
            isLoading={criando}
            loadingText="Criando..."
            isDisabled={itens.length === 0}
          >
            Criar Lista
          </Button>
        </HStack>

        {/* Informações da Lista */}
        <Card mb={6} boxShadow="lg">
          <CardBody>
            <Heading size="md" mb={4} color="#01695E">Informações da Lista</Heading>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Fornecedor</FormLabel>
                <Input
                  value={fornecedor}
                  onChange={(e) => setFornecedor(e.target.value)}
                  placeholder="Digite o nome do fornecedor (ex: VANNUCCI, MORELATE, LNG, CBA, MSAM, BLPARTS)"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Por enquanto, digite o nome. Em breve teremos seleção de fornecedores.
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Data da Lista</FormLabel>
                <Input
                  type="date"
                  value={dataLista}
                  onChange={(e) => setDataLista(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Observações (opcional)</FormLabel>
                <Textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  placeholder="Observações gerais para o fornecedor..."
                />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Adicionar Produtos */}
        <Card mb={6} boxShadow="lg">
          <CardBody>
            <Heading size="md" mb={4} color="#01695E">Adicionar Produtos</Heading>

            <HStack mb={4} spacing={3}>
              <Input
                placeholder="Digite o código do produto"
                value={codigoBusca}
                onChange={(e) => setCodigoBusca(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBuscarProduto()}
              />
              <Button
                leftIcon={<FiSearch />}
                onClick={handleBuscarProduto}
                isLoading={buscandoProduto}
                colorScheme="blue"
              >
                Buscar
              </Button>
            </HStack>

            {produtoEncontrado && (
              <Alert status="success" borderRadius="md" mb={4}>
                <VStack align="stretch" w="100%" spacing={2}>
                  <HStack justifyContent="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold">{produtoEncontrado.nome}</Text>
                      <Text fontSize="sm" color="gray.600">
                        Código: {produtoEncontrado.codigo_global || produtoEncontrado.codigo}
                      </Text>
                    </VStack>
                    <Button
                      size="sm"
                      leftIcon={<FiPlus />}
                      colorScheme="green"
                      onClick={handleAdicionarItem}
                    >
                      Adicionar
                    </Button>
                  </HStack>
                </VStack>
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* Lista de Itens */}
        <Card boxShadow="lg">
          <CardBody>
            <HStack justifyContent="space-between" mb={4}>
              <Heading size="md" color="#01695E">Itens da Lista</Heading>
              <Badge colorScheme="blue" fontSize="lg" px={3}>
                {itens.length} {itens.length === 1 ? 'item' : 'itens'}
              </Badge>
            </HStack>

            {itens.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nenhum item adicionado ainda. Use o campo acima para buscar e adicionar produtos.
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Produto</Th>
                      <Th>Código</Th>
                      <Th isNumeric>Qtd</Th>
                      <Th>Marca</Th>
                      <Th isNumeric>Preço Ref.</Th>
                      <Th>Urgente</Th>
                      <Th>Observações</Th>
                      <Th>Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {itens.map((item, index) => (
                      <Tr key={index}>
                        <Td>
                          <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                            {item.nome_produto}
                          </Text>
                        </Td>
                        <Td fontSize="sm">{item.codigo_global}</Td>
                        <Td isNumeric>
                          <Input
                            type="number"
                            value={item.quantidade_solicitada}
                            onChange={(e) => handleUpdateItem(index, "quantidade_solicitada", e.target.value)}
                            size="sm"
                            w="70px"
                            min={1}
                          />
                        </Td>
                        <Td>
                          <Input
                            value={item.marca_solicitada}
                            onChange={(e) => handleUpdateItem(index, "marca_solicitada", e.target.value)}
                            size="sm"
                            w="100px"
                          />
                        </Td>
                        <Td isNumeric>
                          <Input
                            type="number"
                            value={item.preco_referencia}
                            onChange={(e) => handleUpdateItem(index, "preco_referencia", e.target.value)}
                            size="sm"
                            w="90px"
                            step="0.01"
                          />
                        </Td>
                        <Td>
                          <Checkbox
                            isChecked={item.urgente}
                            onChange={(e) => handleUpdateItem(index, "urgente", e.target.checked)}
                            colorScheme="red"
                          />
                        </Td>
                        <Td>
                          <Input
                            value={item.observacoes}
                            onChange={(e) => handleUpdateItem(index, "observacoes", e.target.value)}
                            size="sm"
                            placeholder="Opcional"
                            w="150px"
                          />
                        </Td>
                        <Td>
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleRemoverItem(index)}
                            aria-label="Remover item"
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
}
